import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Upload, Images, Search, Check, X, Loader2 } from 'lucide-react';
import { mediaUrl } from '../utils/mediaUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Two clearly separate actions: Upload New Image, or Choose Existing Image.
 * Choosing an existing image records a reference only and performs no upload.
 */
export const MediaPicker = ({ token, value, imageRef, purpose = '', onChange, idPrefix = 'media' }) => {
  const [browsing, setBrowsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const currentPreview = mediaUrl(imageRef || value, 'thumb');

  const loadAssets = async (term = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '60' });
      if (term) params.set('search', term);
      const res = await fetch(`${API_URL}/api/media/assets?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setAssets(data.assets || []);
        setTotal(data.total || 0);
      } else {
        toast.error(data.detail || 'Could not load the media library');
      }
    } catch (e) {
      toast.error('Network error loading the media library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (browsing) loadAssets(search); /* eslint-disable-next-line */ }, [browsing]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_URL}/api/media/upload?purpose=${encodeURIComponent(purpose)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json();
      if (res.ok) {
        onChange({ url: data.url, image_ref: data.image_ref });
        toast.success(data.deduplicated
          ? 'This image was already stored — reused the existing original, no extra storage used'
          : 'Image uploaded');
      } else {
        toast.error(data.detail || 'Upload failed');
      }
    } catch (e) {
      toast.error('Network error uploading the image');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const chooseExisting = (asset) => {
    onChange({
      url: asset.secure_url,
      image_ref: {
        asset_id: asset.id,
        public_id: asset.public_id,
        version: asset.version,
        format: asset.format,
        sha256: asset.sha256,
        url: asset.secure_url,
      },
    });
    setBrowsing(false);
    toast.success('Existing image selected — nothing was uploaded');
  };

  return (
    <div data-testid={`${idPrefix}-media-picker`}>
      <div className="flex flex-wrap items-center gap-3">
        {currentPreview ? (
          <img
            src={currentPreview}
            alt="Selected"
            className="w-16 h-16 object-cover rounded border border-stone-200"
            data-testid={`${idPrefix}-media-preview`}
          />
        ) : (
          <div className="w-16 h-16 rounded border border-dashed border-stone-300 flex items-center justify-center text-stone-400">
            <Images size={18} />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <label>
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
              data-testid={`${idPrefix}-media-upload-input`}
            />
            <Button asChild variant="outline" size="sm" disabled={uploading}>
              <span data-testid={`${idPrefix}-media-upload-btn`}>
                {uploading
                  ? <><Loader2 size={14} className="mr-1 animate-spin" /> Uploading...</>
                  : <><Upload size={14} className="mr-1" /> Upload New Image</>}
              </span>
            </Button>
          </label>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setBrowsing(true)}
            data-testid={`${idPrefix}-media-browse-btn`}
          >
            <Images size={14} className="mr-1" /> Choose Existing Image
          </Button>

          {(value || imageRef) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-stone-500"
              onClick={() => onChange({ url: '', image_ref: null })}
              data-testid={`${idPrefix}-media-clear-btn`}
            >
              <X size={14} className="mr-1" /> Remove
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs text-stone-500 mt-1">
        Re-uploading the same photo reuses the stored original — it never costs extra storage.
      </p>

      <Dialog open={browsing} onOpenChange={setBrowsing}>
        <DialogContent className="max-w-3xl" data-testid={`${idPrefix}-media-browse-dialog`}>
          <DialogHeader>
            <DialogTitle>Choose Existing Image</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') loadAssets(search); }}
                placeholder="Search by filename or purpose..."
                className="pl-9"
                data-testid={`${idPrefix}-media-search`}
              />
            </div>
            <Button variant="outline" onClick={() => loadAssets(search)} disabled={loading}>
              {loading ? <Loader2 size={15} className="animate-spin" /> : 'Search'}
            </Button>
          </div>
          <p className="text-xs text-stone-500">{total} image(s) in the library. Selecting one records a reference only — no upload.</p>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3 max-h-[55vh] overflow-y-auto">
            {assets.map((asset) => (
              <button
                key={asset.id}
                type="button"
                onClick={() => chooseExisting(asset)}
                className="group relative border border-stone-200 rounded overflow-hidden hover:border-cafa-red focus:outline-none focus:ring-2 focus:ring-cafa-red"
                title={asset.original_filename || asset.public_id}
                data-testid={`media-asset-${asset.id}`}
              >
                <img
                  src={mediaUrl(asset, 'thumb')}
                  alt={asset.original_filename || asset.public_id}
                  className="w-full h-24 object-cover"
                  loading="lazy"
                />
                <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs">
                  <Check size={16} className="mr-1" /> Select
                </span>
              </button>
            ))}
            {!loading && assets.length === 0 && (
              <p className="col-span-full text-sm text-stone-500 py-6 text-center">
                No images tracked yet. Upload one from any page that uses the Media Picker.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
